import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from 'shared/context/AuthContext';
import API from 'shared/api';
import {
  FiSearch,
  FiX,
  FiChevronDown,
  FiUserPlus,
  FiShield,
  FiUsers,
  FiUser,
  FiMail,
  FiMapPin,
  FiEdit2,
  FiTrash2,
  FiCheck,
  FiLock,
  FiGrid,
} from 'react-icons/fi';

const ROL_LABELS = {
  admin: 'Admin',
  supervisor: 'Supervisor',
  asesor: 'Asesor',
  delivery: 'Delivery',
  armador: 'Armador',
  cocinero: 'Cocinero',
  mesero: 'Mesero',
};
const DRAFT_KEY = 'agorax_draft_usuario';
const EMPTY_USER = {
  cedula: '',
  nombre: '',
  usuario: '',
  clave: '',
  correo: '',
  codigo_sede: '',
  rol: 'asesor',
  role_id: '',
};
const ROL_COLORS = {
  admin: '#ef4444',
  supervisor: '#f59e0b',
  asesor: '#3b82f6',
  delivery: '#10b981',
  armador: '#f97316',
  cocinero: '#8b5cf6',
  mesero: '#ec4899',
};
const ROLES_RESTAURANTE = [
  'admin',
  'supervisor',
  'asesor',
  'delivery',
  'armador',
  'cocinero',
  'mesero',
];
const ROLES_NO_RESTAURANTE = ['admin', 'supervisor', 'asesor', 'delivery', 'armador'];

const CAT_COLORS = {
  'Chat y Pedidos': '#8b5cf6',
  Inventario: '#3b82f6',
  Compras: '#0d9488',
  'Tesorería': '#2563eb',
  Produccion: '#0ea5e9',
  Ventas: '#10b981',
  Clientes: '#f59e0b',
  Usuarios: '#ef4444',
  Sedes: '#6366f1',
  Plan: '#ec4899',
  'Bot IA': '#06b6d4',
  Config: '#14b8a6',
  Dashboard: '#f97316',
};
const CAT_BG = {
  'Chat y Pedidos': '#f5f3ff',
  Inventario: '#eff6ff',
  Compras: '#f0fdfa',
  'Tesorería': '#eff6ff',
  Produccion: '#f0f9ff',
  Ventas: '#ecfdf5',
  Clientes: '#fffbeb',
  Usuarios: '#fef2f2',
  Sedes: '#eef2ff',
  Plan: '#fdf2f8',
  'Bot IA': '#ecfeff',
  Config: '#f0fdfa',
  Dashboard: '#fff7ed',
};
const CAT_ICONS = {
  'Chat y Pedidos': '\u{1F4AC}',
  Inventario: '\u{1F4E6}',
  Compras: '\u{1F6D2}',
  'Tesorería': '\u{1F4B3}',
  Produccion: '\u{1F9EA}',
  Ventas: '\u{1F4B0}',
  Clientes: '\u{1F465}',
  Usuarios: '\u{1F6E1}',
  Sedes: '\u{1F3E2}',
  Plan: '\u{1F4C8}',
  'Bot IA': '\u{1F916}',
  Config: '\u2699',
  Dashboard: '\u{1F4CA}',
};

function CheckBox({ checked, indeterminate, color, onChange, size = 18 }) {
  return (
    <label
      style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
      onClick={(e) => e.stopPropagation()}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: size + 'px',
          height: size + 'px',
          borderRadius: size >= 22 ? '7px' : '5px',
          flexShrink: 0,
          border: `2px solid ${checked || indeterminate ? color || 'var(--agx-pri2)' : 'var(--agx-skeleton)'}`,
          background: checked ? color || 'var(--agx-pri2)' : 'var(--agx-surface)',
          color: '#fff',
          fontSize: size >= 22 ? '13px' : '11px',
          fontWeight: 700,
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          boxShadow: checked ? `0 0 0 3px ${color || 'var(--agx-pri2)'}30` : 'none',
        }}
        onClick={onChange}
      >
        {checked ? '\u2713' : indeterminate ? '\u2013' : ''}
      </span>
    </label>
  );
}

const CAT_ORDER = [
  'Chat y Pedidos',
  'Dashboard',
  'Inventario',
  'Compras',
  'Tesorería',
  'Produccion',
  'Ventas',
  'Clientes',
  'Vendedores',
  'Usuarios',
  'Sedes',
  'Plan',
  'Bot IA',
  'Config',
  'Reportes',
  'Asistencia',
  'Cierre de Caja',
];

function buildTree(permisos, seleccionados) {
  const cats = {};
  permisos.forEach((p) => {
    const cat = p.categoria || 'Otros';
    if (!cats[cat]) cats[cat] = { funcional: [], visibilidad: {} };
    if (p.tipo === 'visibilidad') {
      const padre = p.categoria_padre || 'general';
      if (!cats[cat].visibilidad[padre]) cats[cat].visibilidad[padre] = [];
      cats[cat].visibilidad[padre].push(p);
    } else {
      cats[cat].funcional.push(p);
    }
  });
  return Object.keys(cats)
    .sort((a, b) => {
      const ia = CAT_ORDER.indexOf(a);
      const ib = CAT_ORDER.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    })
    .map((catName) => {
      const cat = cats[catName];
      const allChildren = [
        ...cat.funcional.map((p) => p.codigo),
        ...Object.values(cat.visibilidad)
          .flat()
          .map((p) => p.codigo),
      ];
      const checked = allChildren.filter((c) => seleccionados[c]);
      const state =
        checked.length === 0 ? 'none' : checked.length === allChildren.length ? 'all' : 'some';
      const result = {
        nombre: catName,
        funcional: cat.funcional,
        visibilidad: cat.visibilidad,
        allChildren,
        state,
      };
      if (catName === 'Inventario') {
        const secciones = cat.funcional.filter((p) => p.codigo.startsWith('inventario.seccion.'));
        const tarjetas = cat.funcional.filter((p) => p.codigo.startsWith('inventario.tarjeta.'));
        const acciones = cat.funcional.filter(
          (p) => !p.codigo.startsWith('inventario.seccion.') && !p.codigo.startsWith('inventario.tarjeta.')
        );
        const infoVis = Object.values(cat.visibilidad || {}).flat();
        result.subgrupos = [
          { nombre: 'Secciones', items: secciones },
          { nombre: 'Tarjetas', items: tarjetas },
          { nombre: 'Acciones', items: acciones },
          { nombre: 'Info del producto', items: infoVis },
        ];
      }
      if (catName === 'Produccion') {
        const secciones = cat.funcional.filter((p) => p.codigo.startsWith('produccion.seccion.'));
        const acciones = cat.funcional.filter((p) => !p.codigo.startsWith('produccion.seccion.'));
        const infoVis = Object.values(cat.visibilidad || {}).flat();
        result.subgrupos = [
          { nombre: 'Secciones', items: secciones },
          { nombre: 'Acciones', items: acciones },
          { nombre: 'Visibilidad', items: infoVis },
        ];
      }
      if (catName === 'Compras') {
        const secciones = cat.funcional.filter((p) => p.codigo.startsWith('compras.seccion.'));
        const acciones = cat.funcional.filter((p) => !p.codigo.startsWith('compras.seccion.'));
        const infoVis = Object.values(cat.visibilidad || {}).flat();
        result.subgrupos = [
          { nombre: 'Secciones', items: secciones },
          { nombre: 'Acciones', items: acciones },
          { nombre: 'Visibilidad', items: infoVis },
        ];
      }
      return result;
    });
}

function PermisoArbol({ arbol, seleccionados, onToggle, expandedCats, onToggleExpand }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {arbol.map((cat) => {
        const catColor = CAT_COLORS[cat.nombre] || '#6b7280';
        const catBg = CAT_BG[cat.nombre] || '#f9fafb';
        const catIcon = CAT_ICONS[cat.nombre] || '\u{1F512}';
        const isExpanded = expandedCats[cat.nombre] !== false;
        const totalChecked = cat.allChildren.filter((c) => seleccionados[c]).length;
        return (
          <div
            key={cat.nombre}
            data-cat={cat.nombre}
            style={{
              background: 'var(--agx-surface)',
              borderRadius: '12px',
              border: '1.5px solid #eef0f4',
              overflow: 'hidden',
              transition: 'box-shadow 0.2s',
              boxShadow: isExpanded ? '0 2px 12px rgba(0,0,0,0.04)' : '0 1px 2px rgba(0,0,0,0.02)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px 16px',
                cursor: 'pointer',
                gap: '10px',
                background: isExpanded ? catBg : '#fff',
                borderLeft: `3px solid ${catColor}`,
                transition: 'background 0.2s',
              }}
              onClick={() => onToggleExpand(cat.nombre)}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <CheckBox
                  checked={cat.state === 'all'}
                  indeterminate={cat.state === 'some'}
                  color={catColor}
                  size={22}
                  onChange={() => {
                    const on = cat.state !== 'all';
                    cat.allChildren.forEach((c) => onToggle(c, on));
                  }}
                />
              </div>
              <span style={{ fontSize: '16px', flexShrink: 0 }}>{catIcon}</span>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: 'var(--agx-text)',
                    letterSpacing: '-0.1px',
                  }}
                >
                  {cat.nombre}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--agx-faint)', marginTop: '1px' }}>
                  {cat.allChildren.length} permisos
                </div>
              </div>
              <div
                style={{
                  padding: '4px 10px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: 700,
                  background: totalChecked === 0 ? 'var(--agx-skeleton)' : 'var(--agx-blue-bg)',
                  color:
                    totalChecked === 0
                      ? 'var(--agx-faint)'
                      : totalChecked === cat.allChildren.length
                        ? catColor
                        : 'var(--agx-muted)',
                  letterSpacing: '-0.2px',
                }}
              >
                {totalChecked}/{cat.allChildren.length}
              </div>
              <span
                style={{
                  color: 'var(--agx-faint)',
                  display: 'flex',
                  flexShrink: 0,
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              >
                <FiChevronDown size={16} />
              </span>
            </div>
            {isExpanded && (
              <div style={{ padding: '12px 16px 16px 52px', borderTop: '1px solid #f3f4f6' }}>
                {cat.funcional.length > 0 && !cat.subgrupos && (
                  <div style={{ marginBottom: '14px' }}>
                    <div
                      style={{
                        fontSize: '10px',
                        fontWeight: 700,
                        color: 'var(--agx-faint)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.8px',
                        marginBottom: '8px',
                      }}
                    >
                      Funcional
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                        gap: '4px',
                      }}
                    >
                      {cat.funcional.map((p) => (
                        <div
                          key={p.codigo}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '7px 10px',
                            gap: '8px',
                            borderRadius: '8px',
                            background: seleccionados[p.codigo] ? catBg : 'transparent',
                            transition: 'background 0.15s',
                          }}
                        >
                          <CheckBox
                            checked={!!seleccionados[p.codigo]}
                            onChange={() => onToggle(p.codigo, !seleccionados[p.codigo])}
                          />
                          <span
                            style={{
                              fontSize: '12px',
                              fontWeight: seleccionados[p.codigo] ? 600 : 400,
                              color: seleccionados[p.codigo] ? '#111827' : '#6b7280',
                              lineHeight: '1.3',
                              flex: 1,
                            }}
                          >
                            {p.nombre}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {cat.subgrupos &&
                  cat.subgrupos.map((sg) => (
                    <div key={sg.nombre} style={{ marginBottom: '14px' }}>
                      <div
                        style={{
                          fontSize: '10px',
                          fontWeight: 700,
                          color: 'var(--agx-faint)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.8px',
                          marginBottom: '8px',
                        }}
                      >
                        {sg.nombre}
                      </div>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                          gap: '4px',
                        }}
                      >
                        {sg.items.map((p) => (
                          <div
                            key={p.codigo}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '7px 10px',
                              gap: '8px',
                              borderRadius: '8px',
                              background: seleccionados[p.codigo] ? catBg : 'transparent',
                              transition: 'background 0.15s',
                            }}
                          >
                            <CheckBox
                              checked={!!seleccionados[p.codigo]}
                              onChange={() => onToggle(p.codigo, !seleccionados[p.codigo])}
                            />
                            <span
                              style={{
                                fontSize: '12px',
                                fontWeight: seleccionados[p.codigo] ? 600 : 400,
                                color: seleccionados[p.codigo] ? '#111827' : '#6b7280',
                                lineHeight: '1.3',
                                flex: 1,
                              }}
                            >
                              {p.nombre}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                {Object.keys(cat.visibilidad).length > 0 && (
                  <div>
                    <div
                      style={{
                        fontSize: '10px',
                        fontWeight: 700,
                        color: 'var(--agx-faint)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.8px',
                        marginBottom: '8px',
                      }}
                    >
                      Visibilidad de campos
                    </div>
                    {Object.entries(cat.visibilidad).map(([padre, perms]) => (
                      <div key={padre} style={{ marginBottom: '8px' }}>
                        <div
                          style={{
                            fontSize: '11px',
                            fontWeight: 600,
                            color: 'var(--agx-muted)',
                            marginBottom: '4px',
                            letterSpacing: '-0.1px',
                          }}
                        >
                          {padre === 'general' ? 'Campos generales' : padre}
                        </div>
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                            gap: '2px',
                            paddingLeft: '8px',
                          }}
                        >
                          {perms.map((p) => (
                            <div
                              key={p.codigo}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '5px 8px',
                                gap: '6px',
                                borderRadius: '6px',
                                background: seleccionados[p.codigo] ? catBg : 'transparent',
                              }}
                            >
                              <CheckBox
                                checked={!!seleccionados[p.codigo]}
                                onChange={() => onToggle(p.codigo, !seleccionados[p.codigo])}
                                size={16}
                              />
                              <span
                                style={{
                                  fontSize: '11px',
                                  color: seleccionados[p.codigo] ? '#374151' : '#9ca3af',
                                  flex: 1,
                                }}
                              >
                                {p.nombre}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function RolesTab({ roles, onRefresh, puedeGestionar }) {
  const [cargandoCat, setCargandoCat] = useState(false);
  const [permisosCatalogo, setPermisosCatalogo] = useState([]);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [rolNombre, setRolNombre] = useState('');
  const [permisosSel, setPermisosSel] = useState({});
  const [expandedCats, setExpandedCats] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [editandoEsSistema, setEditandoEsSistema] = useState(false);
  const [vista, setVista] = useState('grid');
  const [filtro, setFiltro] = useState('');

  const cargarCatalogo = async () => {
    setCargandoCat(true);
    try {
      const res = await API.get('/roles/permisos/definicion');
      if (res.data.status === 'success') setPermisosCatalogo(res.data.data);
    } catch (e) {
      console.error('Error:', e);
    } finally {
      setCargandoCat(false);
    }
  };

  const arbol = buildTree(permisosCatalogo, permisosSel);

  const abrirCrear = async () => {
    setEditando(null);
    setEditandoEsSistema(false);
    setRolNombre('');
    setPermisosSel({});
    setExpandedCats({});
    if (!permisosCatalogo.length) await cargarCatalogo();
    setMostrarForm(true);
  };

  const abrirEditar = async (rol) => {
    setEditando(rol.id);
    setEditandoEsSistema(!!rol.es_default);
    setRolNombre(rol.nombre);
    setExpandedCats({});
    try {
      const res = await API.get(`/roles/${rol.id}`);
      if (res.data.status === 'success') {
        const sel = {};
        (res.data.data.permisos || []).forEach((c) => {
          sel[c] = true;
        });
        setPermisosSel(sel);
      }
    } catch (e) {
      setPermisosSel({});
    }
    if (!permisosCatalogo.length) await cargarCatalogo();
    setMostrarForm(true);
  };

  const guardarRol = async () => {
    if (!rolNombre.trim()) {
      alert('El nombre del rol es requerido');
      return;
    }
    setGuardando(true);
    try {
      const permisos = Object.keys(permisosSel).filter((k) => permisosSel[k]);
      if (editando) {
        await API.put(`/roles/${editando}`, { nombre: rolNombre.trim(), permisos });
      } else {
        await API.post('/roles/crear', { nombre: rolNombre.trim(), permisos });
      }
      setMostrarForm(false);
      onRefresh();
    } catch (e) {
      alert('Error al guardar: ' + (e.response?.data?.detail || e.message));
    } finally {
      setGuardando(false);
    }
  };

  const eliminarRol = async (id, nombre) => {
    if (
      !window.confirm(
        `Eliminar el rol "${nombre}"? Los usuarios con este rol quedaran sin rol asignado.`
      )
    )
      return;
    try {
      await API.delete(`/roles/${id}`);
      onRefresh();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    }
  };

  const togglePermiso = (codigo, on) => {
    setPermisosSel((prev) => ({ ...prev, [codigo]: on }));
  };
  const toggleExpand = (cat) => setExpandedCats((prev) => ({ ...prev, [cat]: !prev[cat] }));

  const filtrados = filtro
    ? roles.filter((r) => r.nombre.toLowerCase().includes(filtro.toLowerCase()))
    : roles;
  const selectedCount = Object.values(permisosSel).filter(Boolean).length;
  const totalCount = permisosCatalogo.length;

  return (
    <>
      <div className="usr-header" style={S.header}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ ...S.iconCircle, background: 'var(--agx-info-bg)', color: '#7c3aed' }}>
              <FiShield size={18} />
            </div>
            <div>
              <h2 style={S.title}>Roles y Permisos</h2>
              <div style={{ fontSize: '12px', color: 'var(--agx-faint)', marginTop: '1px' }}>
                {roles.length} roles definidos
              </div>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setVista(vista === 'grid' ? 'list' : 'grid')}
            style={S.btnIcon}
            title="Cambiar vista"
          >
            <FiGrid size={16} />
          </button>
          {puedeGestionar && (
            <button onClick={abrirCrear} className="usr-btn-add" style={S.btnAdd}>
              + Crear Rol
            </button>
          )}
        </div>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <div style={S.searchWrap}>
          <FiSearch size={14} color="#9ca3af" />
          <input
            placeholder="Buscar roles..."
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            style={S.searchInput}
          />
          {filtro && (
            <button onClick={() => setFiltro('')} style={S.clearBtn}>
              <FiX size={14} />
            </button>
          )}
        </div>
      </div>

      {vista === 'grid' ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '12px',
          }}
        >
          {filtrados.length === 0 ? (
            <div style={{ ...S.empty, gridColumn: '1/-1' }}>
              <FiShield size={40} color="#d1d5db" style={{ marginBottom: '8px' }} />
              <span>{filtro ? 'Sin resultados' : 'No hay roles definidos'}</span>
            </div>
          ) : (
            filtrados.map((r) => {
              const accentColor = r.es_default ? '#6b7280' : '#7c3aed';
              const accentBg = r.es_default ? '#f3f4f6' : '#ede9fe';
              return (
                <div
                  key={r.id}
                  style={{
                    background: 'var(--agx-surface)',
                    borderRadius: '14px',
                    padding: '20px',
                    border: '1.5px solid #eef0f4',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '14px',
                    transition: 'box-shadow 0.15s, border-color 0.15s',
                    cursor: 'default',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = accentColor + '40';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.04)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#eef0f4';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div
                        style={{
                          ...S.iconCircle,
                          background: accentBg,
                          color: accentColor,
                          width: '40px',
                          height: '40px',
                        }}
                      >
                        <FiShield size={18} />
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: '15px',
                            fontWeight: 700,
                            color: 'var(--agx-text)',
                            letterSpacing: '-0.2px',
                          }}
                        >
                          {r.nombre}
                        </div>
                        <div
                          style={{ fontSize: '11px', color: 'var(--agx-faint)', marginTop: '1px' }}
                        >
                          ID #{r.id}
                        </div>
                      </div>
                    </div>
                    <span
                      style={{
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '11px',
                        fontWeight: 600,
                        background: accentBg,
                        color: accentColor,
                        letterSpacing: '-0.1px',
                      }}
                    >
                      {r.es_default ? 'Sistema' : 'Custom'}
                    </span>
                  </div>

                  <div style={{ display: 'flex', gap: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span
                        style={{
                          fontSize: '24px',
                          fontWeight: 700,
                          color: accentColor,
                          lineHeight: '1',
                        }}
                      >
                        {r.total_permisos != null ? r.total_permisos : '...'}
                      </span>
                      <span
                        style={{ fontSize: '11px', color: 'var(--agx-faint)', lineHeight: '1.2' }}
                      >
                        permisos
                        <br />
                        asignados
                      </span>
                    </div>
                    <div
                      style={{
                        width: '1px',
                        background: 'var(--agx-skeleton)',
                        alignSelf: 'stretch',
                      }}
                    />
                    <div style={{ fontSize: '11px', color: 'var(--agx-faint)', lineHeight: '1.6' }}>
                      {r.es_default
                        ? 'Rol predefinido del sistema. Editable.'
                        : 'Rol personalizado creado para tu empresa.'}
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      gap: '6px',
                      paddingTop: '4px',
                      borderTop: '1px solid #f3f4f6',
                    }}
                  >
                    <button
                      onClick={() => abrirEditar(r)}
                      style={{
                        flex: 1,
                        padding: '8px 0',
                        borderRadius: '10px',
                        border: 'none',
                        background: accentBg,
                        color: accentColor,
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 600,
                        fontFamily: 'inherit',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '5px',
                      }}
                    >
                      <FiEdit2 size={13} /> Editar
                    </button>
                    {!r.es_default && (
                      <button
                        onClick={() => eliminarRol(r.id, r.nombre)}
                        style={{
                          padding: '8px 12px',
                          borderRadius: '10px',
                          border: '1.5px solid #fee2e2',
                          background: 'var(--agx-surface)',
                          color: 'var(--agx-red)',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 600,
                          fontFamily: 'inherit',
                        }}
                      >
                        <FiTrash2 size={13} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      ) : (
        <div style={S.tableCard}>
          <table style={S.table}>
            <thead>
              <tr>
                {['Nombre', 'Permisos', 'Tipo', ''].map((h) => (
                  <th key={h} style={S.th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtrados.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    style={{
                      textAlign: 'center',
                      padding: '40px',
                      color: 'var(--agx-faint)',
                      fontSize: '13px',
                    }}
                  >
                    {filtro ? 'Sin resultados' : 'No hay roles definidos. Crea tu primer rol.'}
                  </td>
                </tr>
              ) : (
                filtrados.map((r) => (
                  <tr key={r.id} style={S.tr}>
                    <td
                      style={{
                        ...S.td,
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <div
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: r.es_default ? '#d1d5db' : '#7c3aed',
                          flexShrink: 0,
                        }}
                      />
                      {r.nombre}
                      {r.es_default && (
                        <span
                          style={{ fontSize: '10px', color: 'var(--agx-faint)', fontWeight: 400 }}
                        >
                          (default)
                        </span>
                      )}
                    </td>
                    <td style={{ ...S.td, fontSize: '12px', color: 'var(--agx-muted)' }}>
                      {r.total_permisos != null ? `${r.total_permisos} permisos` : '-'}
                    </td>
                    <td style={S.td}>
                      <span
                        style={{
                          padding: '3px 10px',
                          borderRadius: '20px',
                          fontSize: '11px',
                          fontWeight: 600,
                          background: r.es_default ? '#f3f4f6' : '#ede9fe',
                          color: r.es_default ? '#9ca3af' : '#7c3aed',
                        }}
                      >
                        {r.es_default ? 'Sistema' : 'Custom'}
                      </span>
                    </td>
                    <td style={S.td}>
                      {puedeGestionar && (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button onClick={() => abrirEditar(r)} style={S.btnEdit}>
                            Editar
                          </button>
                          {!r.es_default && (
                            <button onClick={() => eliminarRol(r.id, r.nombre)} style={S.btnDel}>
                              Eliminar
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {mostrarForm && (
        <div style={S.overlay} onClick={() => setMostrarForm(false)}>
          <div className="usr-rol-modal" style={S.rolModal} onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
                flexShrink: 0,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div
                  style={{
                    ...S.iconCircle,
                    background: 'var(--agx-info-bg)',
                    color: '#7c3aed',
                    width: '38px',
                    height: '38px',
                  }}
                >
                  <FiShield size={17} />
                </div>
                <div>
                  <h3
                    style={{
                      fontSize: '18px',
                      fontWeight: 700,
                      color: 'var(--agx-text)',
                      margin: 0,
                      letterSpacing: '-0.2px',
                    }}
                  >
                    {editandoEsSistema
                      ? rolNombre + ' (Sistema)'
                      : editando
                        ? 'Editar Rol'
                        : 'Nuevo Rol'}
                  </h3>
                  <div style={{ fontSize: '12px', color: 'var(--agx-faint)' }}>
                    {editandoEsSistema
                      ? 'Personaliza los permisos de este rol del sistema.'
                      : editando
                        ? 'Modifica los permisos de este rol'
                        : 'Define los permisos para el nuevo rol'}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setMostrarForm(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--agx-faint)',
                  fontSize: '22px',
                  padding: '4px',
                }}
              >
                <FiX />
              </button>
            </div>

            <div style={{ marginBottom: '16px', flexShrink: 0 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                  marginBottom: '4px',
                }}
              >
                <label style={S.label}>Nombre del rol</label>
                {editandoEsSistema && (
                  <span
                    style={{ fontSize: '11px', color: 'var(--agx-faint)', fontStyle: 'italic' }}
                  >
                    Los roles del sistema no se pueden renombrar
                  </span>
                )}
              </div>
              <input
                style={{
                  ...S.input,
                  ...(editandoEsSistema
                    ? { background: 'var(--agx-skeleton)', color: 'var(--agx-muted)' }
                    : {}),
                }}
                value={rolNombre}
                onChange={(e) => setRolNombre(e.target.value)}
                placeholder="Ej: Supervisor de Ventas"
                disabled={editandoEsSistema}
              />
              {editandoEsSistema && (
                <div
                  style={{
                    marginTop: '6px',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    background: 'var(--agx-info-bg)',
                    border: '1px solid var(--agx-border)',
                    fontSize: '12px',
                    color: 'var(--agx-info-text)',
                  }}
                >
                  Rol del sistema: puedes ajustar sus permisos. El nombre no es modificable.
                </div>
              )}
            </div>

            <div
              style={{
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--agx-muted)' }}>
                  Arbol de permisos
                </span>
                <span
                  style={{
                    padding: '3px 10px',
                    borderRadius: '20px',
                    fontSize: '11px',
                    fontWeight: 700,
                    background:
                      selectedCount === 0
                        ? '#f3f4f6'
                        : selectedCount === totalCount
                          ? '#ecfdf5'
                          : '#fef3c7',
                    color:
                      selectedCount === 0
                        ? '#9ca3af'
                        : selectedCount === totalCount
                          ? '#059669'
                          : '#d97706',
                  }}
                >
                  {selectedCount}/{totalCount} activos
                </span>
              </div>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                {arbol.map((cat) => {
                  const checked = cat.allChildren.filter((c2) => permisosSel[c2]).length;
                  const color = CAT_COLORS[cat.nombre] || '#6b7280';
                  return (
                    <div
                      key={cat.nombre}
                      title={cat.nombre}
                      onClick={() => {
                        if (cat.nombre)
                          document
                            .querySelector(`[data-cat="${cat.nombre}"]`)
                            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        background:
                          checked === 0
                            ? '#f3f4f6'
                            : checked === cat.allChildren.length
                              ? color
                              : '#fff',
                        border:
                          checked === 0
                            ? '1.5px solid #e5e7eb'
                            : checked === cat.allChildren.length
                              ? `1.5px solid ${color}`
                              : `1.5px solid ${color}40`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        fontWeight: 700,
                        color: checked === cat.allChildren.length ? '#fff' : color,
                        transition: 'all 0.15s',
                      }}
                    >
                      {checked === 0 ? '' : checked === cat.allChildren.length ? '\u2713' : checked}
                    </div>
                  );
                })}
              </div>
            </div>

            <div
              className="usr-perm-scroll"
              style={{ flex: 1, overflowY: 'auto', minHeight: 0, paddingRight: '4px' }}
            >
              {cargandoCat ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--agx-faint)' }}>
                  Cargando permisos...
                </div>
              ) : (
                <PermisoArbol
                  arbol={arbol}
                  seleccionados={permisosSel}
                  onToggle={togglePermiso}
                  expandedCats={expandedCats}
                  onToggleExpand={toggleExpand}
                />
              )}
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '1px solid #f3f4f6',
                flexShrink: 0,
              }}
            >
              <button onClick={() => setMostrarForm(false)} style={S.btnCancel}>
                Cancelar
              </button>
              <button onClick={guardarRol} style={S.btnSave} disabled={guardando}>
                {guardando ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Usuarios() {
  const { isAdmin, isSupervisor, tienePermiso, tieneVisibilidad, isTipoEmpresa } = useAuth();
  const v = (c) => tieneVisibilidad(c);
  const puedeRoles = tienePermiso('usuarios.roles');
  const puedeGestionar = isAdmin() || tienePermiso('usuarios.gestionar');

  const ROLES_DISPONIBLES = isTipoEmpresa('restaurante') ? ROLES_RESTAURANTE : ROLES_NO_RESTAURANTE;

  const [tab, setTab] = useState('usuarios');
  const [usuarios, setUsuarios] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [roles, setRoles] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [filtroRol, setFiltroRol] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState(null); // { tipo: 'success'|'error', texto: '...' }
  const [form, setForm] = useState(EMPTY_USER);

  // Reabrir el formulario con el borrador pendiente tras una recarga
  const draftRehidratadoRef = useRef(false);
  useEffect(() => {
    if (draftRehidratadoRef.current) return;
    draftRehidratadoRef.current = true;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const draft = { ...EMPTY_USER, ...JSON.parse(raw) };
      const tieneContenido = Object.keys(draft).some(
        (k) => k !== 'rol' && k !== 'role_id' && draft[k] && String(draft[k]).trim() !== ''
      );
      if (tieneContenido) {
        setForm(draft);
        setEditando(null);
        setMostrarForm(true);
      }
    } catch (e) {
      /* noop */
    }
  }, []);

  // Persistir borrador SOLO mientras se crea un usuario nuevo (no en edicion)
  useEffect(() => {
    if (mostrarForm && !editando) {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
      } catch (e) {
        /* noop */
      }
    }
  }, [form, mostrarForm, editando]);

  const mostrarMensaje = (tipo, texto) => {
    setMensaje({ tipo, texto });
    setTimeout(() => setMensaje(null), 6000);
  };

  const cargarUsuarios = async () => {
    try {
      const res = await API.get('/usuarios/listar');
      if (res.data.status === 'success') setUsuarios(res.data.data);
    } catch (e) {
      console.error('Error:', e);
    } finally {
      setCargando(false);
    }
  };
  const cargarSedes = async () => {
    try {
      const res = await API.get('/usuarios/sedes');
      if (res.data.status === 'success') setSedes(res.data.data);
    } catch (e) {
      console.error('Error:', e);
    }
  };
  const cargarRoles = async () => {
    try {
      const res = await API.get('/roles/listar');
      if (res.data.status === 'success') setRoles(res.data.data);
    } catch (e) {
      console.error('Error:', e);
    }
  };

  useEffect(() => {
    if (!isAdmin() && !isSupervisor() && !tienePermiso('usuarios.ver')) return;
    cargarUsuarios();
    cargarSedes();
    cargarRoles();
  }, []); // eslint-disable-line

  const abrirFormNuevo = () => {
    let draft = EMPTY_USER;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) draft = { ...EMPTY_USER, ...JSON.parse(raw) };
    } catch (e) {
      /* noop */
    }
    setEditando(null);
    setForm(draft);
    setMostrarForm(true);
  };

  const abrirFormEditar = (u) => {
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch (e) {
      /* noop */
    }
    setEditando(u.cedula);
    setForm({
      cedula: u.cedula,
      nombre: u.nombre || '',
      usuario: u.usuario || '',
      clave: '',
      correo: u.correo || '',
      codigo_sede: u.codigo_sede || '',
      rol: u.rol || 'asesor',
      role_id: u.role_id || '',
    });
    setMostrarForm(true);
  };

  const guardar = async () => {
    if (!form.cedula || !form.nombre || !form.usuario) {
      mostrarMensaje('error', 'Cedula, Nombre y Usuario son requeridos');
      return;
    }
    setGuardando(true);
    try {
      const params = new URLSearchParams();
      params.append('nombre', form.nombre);
      params.append('usuario', form.usuario);
      params.append('clave', form.clave || '');
      params.append('correo', form.correo || '');
      if (form.codigo_sede) params.append('codigo_sede', form.codigo_sede);
      if (form.role_id) {
        params.append('role_id', form.role_id);
      } else if (form.rol) {
        params.append('rol', form.rol);
      }
      if (editando) {
        const res = await API.put(`/usuarios/modificar/${editando}?${params.toString()}`);
        mostrarMensaje('success', res.data?.message || 'Usuario actualizado');
      } else {
        params.append('cedula', form.cedula);
        const res = await API.post(`/usuarios/agregar?${params.toString()}`);
        mostrarMensaje('success', res.data?.message || 'Usuario creado');
      }
      setMostrarForm(false);
      try {
        localStorage.removeItem(DRAFT_KEY);
      } catch (e) {
        /* noop */
      }
      cargarUsuarios();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      mostrarMensaje('error', msg);
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (cedula) => {
    if (!window.confirm('Eliminar este usuario?')) return;
    setGuardando(true);
    try {
      const res = await API.delete(`/usuarios/eliminar/${cedula}`);
      mostrarMensaje('success', res.data?.message || 'Usuario eliminado');
      cargarUsuarios();
    } catch (e) {
      mostrarMensaje('error', e.response?.data?.detail || e.message || 'Error al eliminar');
    } finally {
      setGuardando(false);
    }
  };

  const getRolName = (u) => u.rol_nombre || ROL_LABELS[u.rol] || u.rol || 'Asesor';
  const getRolColor = (u) => {
    if (u.rol_nombre) return '#6366f1';
    return ROL_COLORS[u.rol] || '#6b7280';
  };

  if (!isAdmin() && !isSupervisor() && !tienePermiso('usuarios.ver'))
    return (
      <div style={S.wrapper}>
        <div style={S.card}>
          <p style={{ color: 'var(--agx-faint)', textAlign: 'center', padding: '40px' }}>
            Acceso denegado
          </p>
        </div>
      </div>
    );
  if (cargando)
    return (
      <div style={S.wrapper}>
        <div
          style={{
            textAlign: 'center',
            padding: '80px 20px',
            color: 'var(--agx-faint)',
            fontSize: '14px',
          }}
        >
          Cargando usuarios...
        </div>
      </div>
    );

  const filtrados = usuarios.filter((u) => {
    const q = busqueda.toLowerCase().trim();
    if (
      q &&
      !(u.nombre || '').toLowerCase().includes(q) &&
      !(u.cedula || '').toLowerCase().includes(q) &&
      !(u.usuario || '').toLowerCase().includes(q)
    )
      return false;
    if (filtroRol && u.rol !== filtroRol && (u.role_id ? String(u.role_id) : '') !== filtroRol)
      return false;
    return true;
  });

  const stats = { total: usuarios.length, rbac: 0, sistema: 0 };
  ROLES_DISPONIBLES.forEach((r) => {
    stats[r] = usuarios.filter((u) => u.rol === r && !u.role_id).length;
  });
  stats.rbac = usuarios.filter((u) => !!u.role_id).length;
  stats.sistema = stats.total - stats.rbac;

  const tabs = [{ key: 'usuarios', label: 'Usuarios', icon: '\u{1F465}' }];
  if (puedeRoles) tabs.push({ key: 'roles', label: 'Roles', icon: '\u{1F6E1}' });

  return (
    <div className="usr-wrapper" style={S.wrapper}>
      <style>{`
        @media (max-width: 768px) {
          .usr-wrapper { padding: 12px 8px !important; }
          .usr-header { flex-direction: column !important; gap: 12px !important; align-items: flex-start !important; }
          .usr-btn-add { width: 100% !important; justify-content: center !important; }
          .usr-stats { grid-template-columns: repeat(2, 1fr) !important; gap: 6px !important; }
          .usr-stat-card { padding: 10px 12px !important; }
          .usr-stat-val { font-size: 20px !important; }
          .usr-filters { flex-direction: column !important; }
          .usr-search-wrap { width: 100% !important; max-width: 100% !important; }
          .usr-table-view { display: none !important; }
          .usr-cards-view { display: block !important; }
          .usr-modal { max-width: 95vw !important; padding: 20px !important; }
          .usr-form-grid { gridTemplateColumns: 1fr !important; }
          .usr-rol-modal { max-width: 95vw !important; width: 100% !important; padding: 16px !important; max-height: 92vh !important; border-radius: 14px 14px 0 0 !important; }
          .usr-tab-bar { gap: 0 !important; }
          .usr-tab-btn { padding: 8px 14px !important; font-size: 12px !important; }
        }
        @media (max-width: 420px) {
          .usr-stats { gridTemplateColumns: 1fr !important; }
          .usr-form-hero { flexDirection: column !important; align-items: center !important; }
        }
        @media (min-width: 769px) {
          .usr-table-view { display: block !important; }
          .usr-cards-view { display: none !important; }
        }
      `}</style>

      <div
        style={{
          display: 'flex',
          gap: '2px',
          marginBottom: '24px',
          background: 'var(--agx-skeleton)',
          borderRadius: '12px',
          padding: '3px',
          width: 'fit-content',
        }}
        className="usr-tab-bar"
      >
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className="usr-tab-btn"
            style={{
              padding: '9px 18px',
              borderRadius: '10px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: tab === t.key ? 600 : 500,
              fontFamily: 'inherit',
              background: tab === t.key ? '#fff' : 'transparent',
              color: tab === t.key ? '#111827' : '#6b7280',
              boxShadow: tab === t.key ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span style={{ fontSize: '14px' }}>{t.icon}</span> {t.label}
          </button>
        ))}
      </div>

      {mensaje && (
        <div
          style={{
            padding: '12px 16px',
            borderRadius: '12px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            fontSize: '13px',
            fontWeight: 500,
            animation: 'usr-slide-in 0.3s ease',
            background: mensaje.tipo === 'success' ? '#ecfdf5' : '#fef2f2',
            color: mensaje.tipo === 'success' ? '#065f46' : '#991b1b',
            border: `1.5px solid ${mensaje.tipo === 'success' ? '#a7f3d0' : '#fecaca'}`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {mensaje.tipo === 'success' ? (
              <span style={{ color: 'var(--agx-green)', fontSize: '16px' }}>&#x2713;</span>
            ) : (
              <span style={{ color: 'var(--agx-red)', fontSize: '16px' }}>&#x26A0;</span>
            )}
            <span>{mensaje.texto}</span>
          </div>
          <button
            onClick={() => setMensaje(null)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: mensaje.tipo === 'success' ? '#6ee7b7' : '#fca5a5',
              fontSize: '18px',
              padding: '0 2px',
              lineHeight: 1,
            }}
          >
            &times;
          </button>
        </div>
      )}

      {tab === 'usuarios' ? (
        <>
          <div className="usr-header" style={S.header}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ ...S.iconCircle, background: 'var(--agx-blue-bg)', color: '#3b82f6' }}>
                <FiUsers size={18} />
              </div>
              <div>
                <h2 style={S.title}>Gestion de Usuarios</h2>
                <div style={{ fontSize: '12px', color: 'var(--agx-faint)', marginTop: '1px' }}>
                  {usuarios.length} usuarios
                </div>
              </div>
            </div>
            {puedeGestionar && (
              <button onClick={abrirFormNuevo} className="usr-btn-add" style={S.btnAdd}>
                <FiUserPlus size={15} /> Agregar Usuario
              </button>
            )}
          </div>

          <div className="usr-stats" style={S.statsGrid}>
            {[
              { key: 'total', label: 'Total', color: '#6366f1', icon: '\u{1F465}' },
              { key: 'rbac', label: 'Con rol RBAC', color: '#10b981', icon: '\u{1F6E1}' },
              { key: 'sistema', label: 'Rol del sistema', color: '#f59e0b', icon: '\u26A0' },
              { key: 'admin', label: 'Admin', color: '#ef4444', icon: '\u{1F451}' },
            ].map((s) => (
              <div
                key={s.key}
                className="usr-stat-card"
                style={{ ...S.statCard, borderTop: `3px solid ${s.color}` }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <div>
                    <div className="usr-stat-val" style={{ ...S.statVal, color: s.color }}>
                      {stats[s.key] || 0}
                    </div>
                    <div style={S.statLabel}>{s.label}</div>
                  </div>
                  <span style={{ fontSize: '18px', opacity: 0.4 }}>{s.icon}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="usr-filters" style={S.filterBar}>
            <div className="usr-search-wrap" style={S.searchWrap}>
              <FiSearch size={14} color="#9ca3af" />
              <input
                placeholder="Buscar por nombre, cedula o usuario..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                style={S.searchInput}
              />
              {busqueda && (
                <button onClick={() => setBusqueda('')} style={S.clearBtn}>
                  <FiX size={14} />
                </button>
              )}
            </div>
            <select
              className="usr-filter-select"
              value={filtroRol}
              onChange={(e) => setFiltroRol(e.target.value)}
              style={S.filterSelect}
            >
              <option value="">Todos los roles</option>
              {roles.length > 0 && (
                <optgroup label="Roles personalizados">
                  {roles.map((r) => (
                    <option key={r.id} value={String(r.id)}>
                      {r.nombre}
                    </option>
                  ))}
                </optgroup>
              )}
              <optgroup label="Roles del sistema">
                {ROLES_DISPONIBLES.map((r) => (
                  <option key={r} value={r}>
                    {ROL_LABELS[r]}
                  </option>
                ))}
              </optgroup>
            </select>
          </div>

          <div className="usr-table-view" style={S.tableCard}>
            <table style={S.table}>
              <thead>
                <tr>
                  {[
                    'Cedula',
                    'Nombre',
                    v('vis.usuarios.usuario') ? 'Usuario' : null,
                    v('vis.usuarios.email') ? 'Correo' : null,
                    'Rol',
                    v('vis.usuarios.sede') ? 'Sede' : null,
                    '',
                  ]
                    .filter(Boolean)
                    .map((h) => (
                      <th key={h} style={S.th}>
                        {h}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {filtrados.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      style={{
                        textAlign: 'center',
                        padding: '48px',
                        color: 'var(--agx-faint)',
                        fontSize: '13px',
                      }}
                    >
                      <FiUsers
                        size={32}
                        color="#d1d5db"
                        style={{ marginBottom: '8px', display: 'block', margin: '0 auto 8px' }}
                      />
                      {busqueda || filtroRol ? 'Sin resultados' : 'No hay usuarios registrados'}
                    </td>
                  </tr>
                ) : (
                  filtrados.map((u) => {
                    const rName = getRolName(u);
                    const rColor = getRolColor(u);
                    return (
                      <tr key={u.cedula} style={S.tr}>
                        <td
                          style={{
                            ...S.td,
                            fontFamily: "'SF Mono', 'JetBrains Mono', 'Cascadia Code', monospace",
                            fontSize: '12px',
                            fontWeight: 500,
                            color: '#6366f1',
                          }}
                        >
                          {u.cedula}
                        </td>
                        <td style={{ ...S.td, fontWeight: 600, color: 'var(--agx-text)' }}>
                          {u.nombre}
                        </td>
                        {v('vis.usuarios.usuario') && <td style={S.td}>{u.usuario}</td>}
                        {v('vis.usuarios.email') && (
                          <td style={{ ...S.td, fontSize: '12px' }}>{u.correo || '\u2014'}</td>
                        )}
                        <td style={S.td}>
                          <span
                            style={{
                              padding: '4px 12px',
                              borderRadius: '20px',
                              fontSize: '11px',
                              fontWeight: 600,
                              background: rColor + '14',
                              color: rColor,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            <span
                              style={{
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                background: rColor,
                                flexShrink: 0,
                              }}
                            />
                            {rName}
                            {u.role_id &&
                              u.rol &&
                              u.rol_nombre &&
                              ROL_LABELS[u.rol] !== u.rol_nombre && (
                                <span style={{ fontSize: '9px', opacity: 0.5, fontWeight: 400 }}>
                                  ({ROL_LABELS[u.rol]})
                                </span>
                              )}
                          </span>
                        </td>
                        {v('vis.usuarios.sede') && (
                          <td style={{ ...S.td, fontSize: '12px' }}>
                            {u.sede_nombre || u.codigo_sede || '\u2014'}
                          </td>
                        )}
                        <td style={S.td}>
                          {puedeGestionar && (
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button onClick={() => abrirFormEditar(u)} style={S.btnEdit}>
                                Editar
                              </button>
                              <button onClick={() => eliminar(u.cedula)} style={S.btnDel}>
                                Eliminar
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="usr-cards-view">
            {filtrados.length === 0 ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: 'var(--agx-faint)',
                  fontSize: '13px',
                  background: 'var(--agx-surface)',
                  borderRadius: '14px',
                  border: '1.5px solid #eef0f4',
                }}
              >
                <FiUsers size={32} color="#d1d5db" style={{ marginBottom: '8px' }} />
                <div>{busqueda || filtroRol ? 'Sin resultados' : 'No hay usuarios'}</div>
              </div>
            ) : (
              filtrados.map((u) => {
                const rName = getRolName(u);
                const rColor = getRolColor(u);
                return (
                  <div key={u.cedula} style={S.mobileCard}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '12px',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '12px',
                            background: rColor + '18',
                            color: rColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '16px',
                            fontWeight: 700,
                          }}
                        >
                          {(u.nombre || '?')[0].toUpperCase()}
                        </div>
                        <div>
                          <div
                            style={{ fontWeight: 600, fontSize: '15px', color: 'var(--agx-text)' }}
                          >
                            {u.nombre}
                          </div>
                          <div
                            style={{
                              fontSize: '12px',
                              color: 'var(--agx-faint)',
                              fontFamily: 'monospace',
                            }}
                          >
                            {u.cedula}
                          </div>
                        </div>
                      </div>
                      <span
                        style={{
                          padding: '3px 10px',
                          borderRadius: '20px',
                          fontSize: '11px',
                          fontWeight: 600,
                          background: rColor + '14',
                          color: rColor,
                        }}
                      >
                        {rName}
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '8px',
                        marginBottom: '12px',
                      }}
                    >
                      {v('vis.usuarios.usuario') && (
                        <div>
                          <span
                            style={{
                              fontSize: '10px',
                              color: 'var(--agx-faint)',
                              textTransform: 'uppercase',
                              letterSpacing: '0.3px',
                            }}
                          >
                            Usuario
                          </span>
                          <div
                            style={{ fontSize: '13px', fontWeight: 500, color: 'var(--agx-muted)' }}
                          >
                            {u.usuario}
                          </div>
                        </div>
                      )}
                      {v('vis.usuarios.email') && (
                        <div>
                          <span
                            style={{
                              fontSize: '10px',
                              color: 'var(--agx-faint)',
                              textTransform: 'uppercase',
                              letterSpacing: '0.3px',
                            }}
                          >
                            Correo
                          </span>
                          <div style={{ fontSize: '13px', color: 'var(--agx-muted)' }}>
                            {u.correo || '\u2014'}
                          </div>
                        </div>
                      )}
                      {v('vis.usuarios.sede') && (
                        <div>
                          <span
                            style={{
                              fontSize: '10px',
                              color: 'var(--agx-faint)',
                              textTransform: 'uppercase',
                              letterSpacing: '0.3px',
                            }}
                          >
                            Sede
                          </span>
                          <div style={{ fontSize: '13px', color: 'var(--agx-muted)' }}>
                            {u.sede_nombre || u.codigo_sede || '\u2014'}
                          </div>
                        </div>
                      )}
                    </div>
                    {puedeGestionar && (
                      <div
                        style={{
                          display: 'flex',
                          gap: '8px',
                          borderTop: '1px solid #f3f4f6',
                          paddingTop: '10px',
                        }}
                      >
                        <button
                          onClick={() => abrirFormEditar(u)}
                          style={{ ...S.btnEdit, flex: 1, textAlign: 'center' }}
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => eliminar(u.cedula)}
                          style={{ ...S.btnDel, flex: 1, textAlign: 'center' }}
                        >
                          Eliminar
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {mostrarForm && (
            <div style={S.overlay} onClick={() => setMostrarForm(false)}>
              <div className="usr-modal" style={S.modal} onClick={(e) => e.stopPropagation()}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '24px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div
                      style={{
                        ...S.iconCircle,
                        background: editando ? '#eff6ff' : '#ecfdf5',
                        color: editando ? '#3b82f6' : '#10b981',
                        width: '38px',
                        height: '38px',
                      }}
                    >
                      {editando ? <FiEdit2 size={17} /> : <FiUserPlus size={17} />}
                    </div>
                    <div>
                      <h3
                        style={{
                          fontSize: '18px',
                          fontWeight: 700,
                          color: 'var(--agx-text)',
                          margin: 0,
                          letterSpacing: '-0.2px',
                        }}
                      >
                        {editando ? 'Editar Usuario' : 'Nuevo Usuario'}
                      </h3>
                      <div style={{ fontSize: '12px', color: 'var(--agx-faint)' }}>
                        {editando
                          ? 'Modifica los datos del usuario'
                          : 'Completa la informacion del nuevo usuario'}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setMostrarForm(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--agx-faint)',
                      fontSize: '22px',
                      padding: '4px',
                    }}
                  >
                    <FiX />
                  </button>
                </div>

                {mensaje && (
                  <div
                    style={{
                      padding: '11px 14px',
                      borderRadius: '10px',
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '10px',
                      fontSize: '13px',
                      fontWeight: 500,
                      background: mensaje.tipo === 'success' ? '#ecfdf5' : '#fef2f2',
                      color: mensaje.tipo === 'success' ? '#065f46' : '#991b1b',
                      border: `1.5px solid ${mensaje.tipo === 'success' ? '#a7f3d0' : '#fecaca'}`,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {mensaje.tipo === 'success' ? (
                        <span
                          style={{ color: 'var(--agx-green)', fontSize: '14px', fontWeight: 700 }}
                        >
                          &#x2713;
                        </span>
                      ) : (
                        <span
                          style={{ color: 'var(--agx-red)', fontSize: '14px', fontWeight: 700 }}
                        >
                          &#x26A0;
                        </span>
                      )}
                      <span>{mensaje.texto}</span>
                    </div>
                    <button
                      onClick={() => setMensaje(null)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: mensaje.tipo === 'success' ? '#6ee7b7' : '#fca5a5',
                        fontSize: '16px',
                        padding: '0 2px',
                        lineHeight: 1,
                      }}
                    >
                      &times;
                    </button>
                  </div>
                )}

                <div
                  style={{
                    display: 'flex',
                    gap: '24px',
                    flexDirection:
                      typeof window !== 'undefined' && window.innerWidth < 500 ? 'column' : 'row',
                  }}
                  className="usr-form-hero"
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '20px',
                        background: form.nombre ? '#eff6ff' : '#f3f4f6',
                        color: form.nombre ? '#3b82f6' : '#d1d5db',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '28px',
                        fontWeight: 700,
                      }}
                    >
                      {form.nombre ? form.nombre[0].toUpperCase() : <FiUser size={28} />}
                    </div>
                    {editando && (
                      <span
                        style={{
                          fontSize: '11px',
                          color: 'var(--agx-faint)',
                          fontFamily: 'monospace',
                        }}
                      >
                        ID: {editando}
                      </span>
                    )}
                  </div>

                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '14px',
                      minWidth: 0,
                    }}
                  >
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns:
                          typeof window !== 'undefined' && window.innerWidth < 500
                            ? '1fr'
                            : '1fr 1fr',
                        gap: '12px',
                      }}
                    >
                      <div style={S.fg}>
                        <label style={S.label}>Cedula *</label>
                        <div style={S.inputWrap}>
                          <FiUser size={14} color="#9ca3af" style={{ flexShrink: 0 }} />
                          <input
                            style={{ ...S.inputInner, background: 'transparent' }}
                            value={form.cedula}
                            onChange={(e) => setForm({ ...form, cedula: e.target.value })}
                            disabled={!!editando}
                            placeholder="V12345678"
                          />
                        </div>
                      </div>
                      <div style={S.fg}>
                        <label style={S.label}>Nombre completo *</label>
                        <div style={S.inputWrap}>
                          <FiEdit2 size={14} color="#9ca3af" style={{ flexShrink: 0 }} />
                          <input
                            style={{ ...S.inputInner, background: 'transparent' }}
                            value={form.nombre}
                            onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                            placeholder="Maria Perez"
                          />
                        </div>
                      </div>
                      <div style={S.fg}>
                        <label style={S.label}>Usuario *</label>
                        <div style={S.inputWrap}>
                          <FiUser size={14} color="#9ca3af" style={{ flexShrink: 0 }} />
                          <input
                            style={{ ...S.inputInner, background: 'transparent' }}
                            value={form.usuario}
                            onChange={(e) => setForm({ ...form, usuario: e.target.value })}
                            placeholder="maria.perez"
                          />
                        </div>
                      </div>
                      <div style={S.fg}>
                        <label style={S.label}>
                          Contraseña{' '}
                          {editando && (
                            <span style={{ fontWeight: 400, color: 'var(--agx-faint)' }}>
                              (vacio = sin cambio)
                            </span>
                          )}
                        </label>
                        <div style={S.inputWrap}>
                          <FiLock size={14} color="#9ca3af" style={{ flexShrink: 0 }} />
                          <input
                            style={{ ...S.inputInner, background: 'transparent' }}
                            type="password"
                            value={form.clave}
                            onChange={(e) => setForm({ ...form, clave: e.target.value })}
                            placeholder={editando ? 'Dejar vacío...' : 'Contraseña'}
                          />
                        </div>
                      </div>
                      <div style={S.fg}>
                        <label style={S.label}>Correo</label>
                        <div style={S.inputWrap}>
                          <FiMail size={14} color="#9ca3af" style={{ flexShrink: 0 }} />
                          <input
                            style={{ ...S.inputInner, background: 'transparent' }}
                            value={form.correo}
                            onChange={(e) => setForm({ ...form, correo: e.target.value })}
                            placeholder="maria@empresa.com"
                          />
                        </div>
                      </div>
                      <div style={S.fg}>
                        <label style={S.label}>Rol</label>
                        <select
                          style={S.select}
                          value={form.role_id || form.rol}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val && isNaN(parseInt(val)))
                              setForm({ ...form, rol: val, role_id: '' });
                            else setForm({ ...form, role_id: val, rol: '' });
                          }}
                        >
                          <option value="">Sin rol asignado</option>
                          {roles.length > 0 && (
                            <optgroup label="Roles personalizados (RBAC)">
                              {roles.map((r) => (
                                <option key={r.id} value={String(r.id)}>
                                  {r.nombre} {r.es_default ? '(sistema)' : ''}
                                </option>
                              ))}
                            </optgroup>
                          )}
                          <optgroup label="Roles del sistema">
                            <option value="asesor">Asesor</option>
                            <option value="supervisor">Supervisor</option>
                            <option value="delivery">Delivery</option>
                            <option value="armador">Armador</option>
                            {isAdmin() && <option value="admin">Admin</option>}
                          </optgroup>
                        </select>
                      </div>
                    </div>
                    <div style={S.fg}>
                      <label style={S.label}>
                        Sede{' '}
                        {isSupervisor() && (
                          <span style={{ fontWeight: 400, color: 'var(--agx-faint)' }}>
                            (asignada automaticamente)
                          </span>
                        )}
                      </label>
                      <div style={S.inputWrap}>
                        <FiMapPin size={14} color="#9ca3af" style={{ flexShrink: 0 }} />
                        <select
                          style={{
                            ...S.select,
                            flex: 1,
                            border: 'none',
                            padding: '0',
                            outline: 'none',
                            boxShadow: 'none',
                            background: isSupervisor() ? '#f8f9fb' : 'transparent',
                          }}
                          value={form.codigo_sede}
                          onChange={(e) => setForm({ ...form, codigo_sede: e.target.value })}
                          disabled={isSupervisor()}
                        >
                          <option value="">Sin sede</option>
                          {sedes
                            .filter((s) => s.activa !== false)
                            .map((s) => (
                              <option key={s.codigo} value={s.codigo}>
                                {s.nombre} ({s.codigo})
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '8px',
                    marginTop: '20px',
                    paddingTop: '16px',
                    borderTop: '1px solid #f3f4f6',
                  }}
                >
                  <button
                    onClick={() => setMostrarForm(false)}
                    style={S.btnCancel}
                    disabled={guardando}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={guardar}
                    style={{ ...S.btnSave, opacity: guardando ? 0.7 : 1 }}
                    disabled={guardando}
                  >
                    {guardando ? (
                      <>Guardando...</>
                    ) : (
                      <>
                        <FiCheck size={15} style={{ marginRight: '4px' }} />
                        {editando ? 'Actualizar' : 'Crear Usuario'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <RolesTab roles={roles} onRefresh={cargarRoles} puedeGestionar={puedeGestionar} />
      )}
    </div>
  );
}

const S = {
  wrapper: {
    padding: '24px 28px',
    background: 'var(--agx-bg)',
    minHeight: '100vh',
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
  },
  card: {
    background: 'var(--agx-surface)',
    borderRadius: '16px',
    padding: '28px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)',
    border: '1.5px solid var(--agx-border)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  title: {
    fontSize: '22px',
    fontWeight: 700,
    color: 'var(--agx-text)',
    letterSpacing: '-0.3px',
    margin: 0,
  },
  iconCircle: {
    width: '36px',
    height: '36px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  btnAdd: {
    padding: '10px 20px',
    borderRadius: '12px',
    border: 'none',
    background: 'var(--agx-text)',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'all 0.15s',
    letterSpacing: '-0.1px',
  },
  btnIcon: {
    padding: '10px',
    borderRadius: '12px',
    border: '1.5px solid var(--agx-border)',
    background: 'var(--agx-surface)',
    color: 'var(--agx-muted)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'inherit',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
    gap: '10px',
    marginBottom: '20px',
  },
  statCard: {
    background: 'var(--agx-surface)',
    borderRadius: '12px',
    padding: '16px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
    border: '1.5px solid var(--agx-border)',
  },
  statVal: { fontSize: '26px', fontWeight: 700, lineHeight: '1.1' },
  statLabel: {
    fontSize: '11px',
    color: 'var(--agx-faint)',
    fontWeight: 500,
    marginTop: '2px',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  filterBar: {
    display: 'flex',
    gap: '10px',
    marginBottom: '16px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  searchWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 14px',
    borderRadius: '12px',
    border: '1.5px solid var(--agx-border)',
    background: 'var(--agx-surface)',
    flex: 1,
    maxWidth: '380px',
    minWidth: '180px',
  },
  searchInput: {
    border: 'none',
    outline: 'none',
    fontSize: '13px',
    fontFamily: 'inherit',
    width: '100%',
    color: 'var(--agx-text)',
    background: 'transparent',
  },
  clearBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--agx-faint)',
    padding: '2px',
    display: 'flex',
    borderRadius: '50%',
  },
  filterSelect: {
    padding: '10px 14px',
    borderRadius: '12px',
    border: '1.5px solid var(--agx-border)',
    fontSize: '13px',
    fontFamily: 'inherit',
    outline: 'none',
    background: 'var(--agx-surface)',
    color: 'var(--agx-muted)',
    minWidth: '140px',
  },
  tableCard: {
    background: 'var(--agx-surface)',
    borderRadius: '14px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
    border: '1.5px solid var(--agx-border)',
    overflow: 'auto',
  },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '600px' },
  th: {
    textAlign: 'left',
    padding: '11px 16px',
    fontSize: '10px',
    fontWeight: 700,
    color: 'var(--agx-faint)',
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    borderBottom: '1px solid var(--agx-border)',
    background: 'var(--agx-bg2)',
  },
  tr: { borderBottom: '1px solid var(--agx-border)', transition: 'background 0.1s' },
  td: {
    padding: '12px 16px',
    fontSize: '13px',
    color: 'var(--agx-muted)',
    verticalAlign: 'middle',
  },
  btnEdit: {
    padding: '6px 14px',
    borderRadius: '8px',
    border: 'none',
    background: 'var(--agx-blue-bg)',
    color: 'var(--agx-pri2)',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
    fontFamily: 'inherit',
    letterSpacing: '-0.1px',
  },
  btnDel: {
    padding: '6px 14px',
    borderRadius: '8px',
    border: 'none',
    background: 'var(--agx-red-bg)',
    color: 'var(--agx-red)',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
    fontFamily: 'inherit',
    letterSpacing: '-0.1px',
  },
  mobileCard: {
    background: 'var(--agx-surface)',
    borderRadius: '14px',
    padding: '16px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
    border: '1.5px solid var(--agx-border)',
    marginBottom: '10px',
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px',
    color: 'var(--agx-faint)',
    fontSize: '13px',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.55)',
    backdropFilter: 'blur(2px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '16px',
  },
  modal: {
    background: 'var(--agx-surface)',
    borderRadius: '20px',
    padding: '28px',
    width: '100%',
    maxWidth: '620px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  rolModal: {
    background: 'var(--agx-surface)',
    borderRadius: '20px',
    padding: '24px 28px',
    width: '100%',
    maxWidth: '760px',
    maxHeight: '88vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
  },
  fg: { display: 'flex', flexDirection: 'column', gap: '4px' },
  label: {
    fontSize: '11px',
    fontWeight: 700,
    color: 'var(--agx-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  input: {
    width: '100%',
    padding: '11px 14px',
    borderRadius: '12px',
    border: '1.5px solid var(--agx-border)',
    fontSize: '13px',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
    color: 'var(--agx-text)',
    background: 'var(--agx-surface)',
    transition: 'border-color 0.15s',
  },
  inputWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    padding: '11px 14px',
    borderRadius: '12px',
    border: '1.5px solid var(--agx-border)',
    background: 'var(--agx-surface)',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  },
  inputInner: {
    border: 'none',
    outline: 'none',
    fontSize: '13px',
    fontFamily: 'inherit',
    width: '100%',
    color: 'var(--agx-text)',
  },
  select: {
    width: '100%',
    padding: '11px 14px',
    borderRadius: '12px',
    border: '1.5px solid var(--agx-border)',
    fontSize: '13px',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
    color: 'var(--agx-text)',
    background: 'var(--agx-surface)',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    cursor: 'pointer',
  },
  btnCancel: {
    padding: '10px 20px',
    borderRadius: '12px',
    border: '1.5px solid var(--agx-border)',
    background: 'var(--agx-surface)',
    color: 'var(--agx-muted)',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    fontFamily: 'inherit',
    letterSpacing: '-0.1px',
  },
  btnSave: {
    padding: '10px 22px',
    borderRadius: '12px',
    border: 'none',
    background: 'var(--agx-text)',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    letterSpacing: '-0.1px',
  },
};

export default Usuarios;
